import './App.css';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import Home from 'pages/Home/Home';
import Login from 'pages/Auth/Login';
import Register from 'pages/Auth/Register';
import AcceptMemberInvite from 'pages/Auth/AcceptMemberInvite';
import AcceptInvite from 'pages/Auth/AcceptInvite';
import Layout from 'components/Common/Layout';
import ForgotPassword from 'pages/Auth/ForgotPassword';
import VerifyEmailPin from 'pages/Auth/VerifyEmailPin';
import Verify2FAPin from 'pages/Auth/Verify2FAPin';
import SendInvoice from 'pages/Invoice/SendInvoice';
import InvoiceHistory from 'pages/Invoice/InvoiceHistory';
import Staff from 'pages/Staff/Staff';
import CreateNewStaff from 'pages/Staff/CreateNewStaff';
import MyAccount from 'pages/Account/MyAccount';
import MyAccountEditInfo from 'pages/Account/MyAccountEditInfo';
import MyAccountChangePassword from 'pages/Account/MyAccountChangePassword';
import BasketDetail from 'pages/Home/BasketDetail';
import CreateBasket from 'pages/Home/CreateBasket';
import AccountChangePhoto from 'pages/Account/AccountChangePhoto';
import routes from 'constants/routes';
import Community from 'pages/Community/Community';
import ParishonerDetail from 'pages/Community/ParishonerDetail';
import IssueRefund from 'pages/Community/IssueRefund';
import ParishonerWallet from 'pages/Wallet/ParishWallet';
import WalletBaskets from 'pages/Wallet/WalletBaskets';
import Announcements from 'pages/Annoucement/Announcements';
import AnnouncementDetails from 'pages/Annoucement/AnnouncementDetails';
import CreateAnnouncementGroup from 'pages/Annoucement/CreateAnnouncementGroup';
import AnnouncementGroupDetails from 'pages/Annoucement/AnnouncementGroupDetails';
import Parishes from 'pages/Parishes&Organizations/Parishes';
import OrganizationDetail from 'components/Parishes&Organizations/OrganizationDetails';
import { useDispatch } from 'react-redux';
import ProtectedRoute from 'auth/ProtectedRoute';
import {
    RedirectRemovedBasketWithdraw,
    RedirectRemovedWalletMain,
    RedirectRemovedWithdrawToBank,
    RedirectTemporarilyHiddenSendMoney,
} from 'auth/RemovedMoneyRedirects';
import { appClient } from 'api/apiClient';
import { AppDispatch } from 'redux/store';
import ViewQRCode from 'pages/QR/ViewQRCode';
import NotFound from 'pages/NotFound/NotFound';
import ErrorBoundary from 'pages/ErrorBoundary';
import WalletInvoiceDetail from 'pages/Wallet/WalletInvoiceDetail';
import PaymentRequestDetail from 'pages/Wallet/PaymentRequestDetail';
import StripeOnboarding from 'pages/Wallet/StripeOnboarding';
import Reports from 'pages/Reports/Reports';
import RequireMerchant from 'auth/RequireMerchant';
import UserDetail from 'pages/User/UserDetail';
import CreateAnnouncementContainer from 'pages/Annoucement/CreateAnnouncementContainer';
import EditAnnouncementContainer from 'pages/Annoucement/EditAnnouncementContainer';
import VerifyPinCode from 'pages/Auth/VerifyPinCode';
import TransactionDetailContainer from 'pages/Transaction/TransactionDetail';

function App() {
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        appClient.setDispatch(dispatch);
    }, [dispatch]);

    return (
        <Router>
            <ErrorBoundary dispatch={dispatch}>
                <Routes>
                    {/* Login Route */}
                    <Route path={routes.auth.login} element={<Login />} />
                    {/* Register is invite-gated in-page (pending staff/member invite token/email); not open signup. */}
                    <Route path={routes.auth.register} element={<Register />} />
                    <Route path={routes.auth.acceptMemberInvite} element={<AcceptMemberInvite />} />
                    <Route path={routes.auth.acceptInvite} element={<AcceptInvite />} />
                    {/* Forgot Password Route */}
                    <Route path={routes.auth.forgotPassword} element={<ForgotPassword />} />
                    <Route path={routes.auth.verifyPin} element={<VerifyPinCode />}></Route>
                    <Route path={routes.auth.verifyEmail} element={<VerifyEmailPin />} />
                    <Route path={routes.auth.verify2fa} element={<Verify2FAPin />} />
                    {/* Change Password Route */}
                    {/* Not wrapped in ProtectedRoute: this page also serves the unauthenticated forgot-password
                        reset flow (type: 'resetPassword' in location.state). It guards the authenticated
                        "change password" case itself instead. */}
                    <Route path={routes.account.changePassword} element={<MyAccountChangePassword />} />

                    {/* Routes With Layout */}
                    <Route element={<Layout />}>
                        {/* Protected Routes */}
                        <Route element={<ProtectedRoute />}>
                            {/* Home Route */}
                            <Route path={routes.home.main} element={<Home />} />
                            {/* Send Invoice Route */}
                            <Route path={routes.sendinvoice.main} element={<SendInvoice />} />
                            {/* Parish-wide invoices, sent by any staff member of the current parish */}
                            <Route path={routes.invoices.list} element={<InvoiceHistory />} />
                            <Route path="/invoices" element={<Navigate to={routes.invoices.list} replace />} />
                            {/* Send Money — temporarily hidden; page kept for later remount */}
                            <Route path={routes.sendmoney.main} element={<RedirectTemporarilyHiddenSendMoney />} />
                            {/* Home - Basket Details Active  */}
                            <Route path={routes.home.basketDetails} element={<BasketDetail />} />
                            {/* Home - Basket withdraw (Remove bucket → redirect) */}
                            <Route path={routes.home.basketwithdrawbalance} element={<RedirectRemovedBasketWithdraw />} />
                            {/* Home - Basket Create  */}
                            <Route path={routes.home.basketCreate} element={<CreateBasket />} />
                            {/* Home - Basket edit  */}
                            <Route path={routes.home.basketUpdate} element={<CreateBasket />} />

                            {/* Staff Route */}
                            <Route element={<RequireMerchant />}>
                                <Route path={routes.staff.main} element={<Staff />} />
                                <Route path={routes.staff.add} element={<CreateNewStaff />} />
                                {/* Phase 11 Ready — Stripe onboarding/status (CLERK+ gate; link is MERCHANT_ADMIN+) */}
                                <Route path={routes.wallet.stripeSetup} element={<StripeOnboarding />} />
                                {/* Parish ledger feed (CLERK+) + aggregate summary (MERCHANT_ADMIN+, gated inside the page) */}
                                <Route path={routes.reports.main} element={<Reports />} />
                            </Route>

                            {/* User */}
                            <Route path={routes.user.userDetail} element={<UserDetail />} />

                            {/* My Account Route */}
                            <Route path={routes.account.main} element={<MyAccount />} />
                            <Route path={routes.account.edit} element={<MyAccountEditInfo />} />
                            <Route path={routes.account.changePhoto} element={<AccountChangePhoto />} />

                            {/* Community Route */}
                            <Route path={routes.community.main} element={<Community />} />
                            <Route path={routes.community.ParishonerDetails} element={<ParishonerDetail />} />
                            <Route path={routes.community.issueRefund} element={<IssueRefund />} />

                            {/* Announcements Route */}
                            <Route path={routes.announcements.main} element={<Announcements />} />
                            <Route path={routes.announcements.details} element={<AnnouncementDetails />} />
                            <Route path={routes.announcements.new} element={<CreateAnnouncementContainer />} />
                            <Route path={routes.announcements.edit} element={<EditAnnouncementContainer />} />
                            <Route path={routes.announcements.createGroup} element={<CreateAnnouncementGroup />} />
                            <Route path={routes.announcements.groupDetails} element={<AnnouncementGroupDetails />} />

                            {/* Wallet — Remove: main SVA + withdraw-to-bank redirect; Freeze/Ready keep routes */}
                            <Route path={routes.wallet.main} element={<RedirectRemovedWalletMain />} />
                            <Route path={routes.wallet.parishWallet} element={<ParishonerWallet />} />
                            <Route path={routes.wallet.withdrawToBank} element={<RedirectRemovedWithdrawToBank />} />
                            <Route path={routes.wallet.givingBasket} element={<WalletBaskets basketType="givingList" />} />
                            <Route path={routes.wallet.paymentBasket} element={<WalletBaskets basketType="paymentList" />} />

                            {/* Parish Wallet - Invoice Details (legacy path → /payment-requests/:id) */}
                            <Route path={routes.wallet.invoiceDetails} element={<WalletInvoiceDetail />} />
                            <Route path={routes.paymentRequests.detail} element={<PaymentRequestDetail />} />

                            {/* Parishes & Organization - Archdiocese */}
                            <Route path={routes.parishes.main} element={<Parishes />} />
                            <Route path={routes.parishes.organizationDetails} element={<OrganizationDetail />} />

                            {/* QR Routes */}
                            <Route path={routes.qr.main} element={<ViewQRCode />} />

                            {/* Transaction Routes */}
                            <Route path={routes.transaction.main} element={<TransactionDetailContainer />} />
                        </Route>
                    </Route>
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </ErrorBoundary>
        </Router>
    );
}

export default App;
